import { ClinicalTrialInventoryType } from '@dynamika-shared/types/clinicalTrialInventory';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { useClinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { FC, useMemo, useState } from 'react';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { getClinicalInventoryColumns } from './ClinicalTrialInventory.columns';
import { twMerge } from '@utils/twMerge';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTeamMembersListSlice } from '@restore/teamMembersList/teamMembersList.slice';
import { CreateItemClinicalTrialInventory } from './CreateItemClinicalTrialInventory';
import { OnSortColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { sortClinicalTrialInventory } from 'src/pages/ClinicalTrial/Readings/ReadingInventory/components/helpers';
import { usePaginationAndSort } from '@restore/helpers';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

type ClinicalTrialInventoryTableType = {
  type: keyof ClinicalTrialInventoryType;
};

export type ClinicalTrialInventoryBaseItemType = {
  name: string;
  code: string;
  description?: string;
  id?: string;
};
export type ClinicalTrialInventoryBaseItemTypeState = {
  mode: 'create' | 'edit';
  data: ClinicalTrialInventoryBaseItemType;
};

const clinicalTrialInventoryMapper: Record<
  keyof ClinicalTrialInventoryType,
  string
> = {
  scoringForms: 'Scoring Forms',
  tags: 'Tags',
  functions: 'Functions',
  measurements: 'Measurements',
  readers: 'Readers',
  instruments: 'Instruments',
  reportTemplates: 'Report Templates',
  dictionaries: 'Dictionaries'
};

export const ClinicalTrialInventoryTable: FC<
  ClinicalTrialInventoryTableType
> = ({ type }) => {

  const isPM = useAuthSlice(s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER);

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { sortBy, setSortBy } = usePaginationAndSort(type);
  const [clinicalTrialInventoryState, actions] = useClinicalTrialInventorySlice(
    (state, actions) => [state, actions]
  );
  const allTeamMembers = useTeamMembersListSlice(state => state.allData) || [];

  const data = useMemo(() => {
    return [...clinicalTrialInventoryState[type] || []].sort(
      sortClinicalTrialInventory(sortBy ?? '')
    );
  }, [clinicalTrialInventoryState, type, sortBy]);

  const [currentItemId, setCurrentItemId] = useState<string>();
  const [createItem, setCreateItem] = useState<boolean>();

  const clinicalInventoryColumns = getClinicalInventoryColumns(
    type,
    {
      edit: item => {
        navigate(`${location.pathname}/${item.id}`, {
          state: { mode: 'edit', data: item },
        });
      },
      delete: item => {
        setCurrentItemId(item.id);
      },
    },
    { allTeamMembers, clinicalTrialInventoryState },
    isPM
  );

  const onSort: OnSortColumnType = (column, direction = 'asc') => {
    setSortBy({ listName: type, newSortBy: `${column},${direction}` });
  };

  const deleteItem = () => {
    actions.deleteClinicalTrialInventoryItem({
      ctId: id as string,
      itemId: currentItemId as string,
      type,
    });
  };

  return (
    <div>
      <form
        id={'clinical-trial-inventory-table'}
        className='mt-3'
        onSubmit={e => {
          e.preventDefault();
          setCreateItem(true);
        }}
      >
        <Table
          columns={clinicalInventoryColumns}
          data={data}
          totalItems={data?.length}
          loading={clinicalTrialInventoryState.loading}
          onSort={onSort}
          sortState={sortBy}
          noContentMessage={ !isPM && (
            <NoContent
              action={() => setCreateItem(true)}
              actionText={`Add ${clinicalTrialInventoryMapper[type]}`}
              iconName={'noContent'}
              title={
                <span className='text-1424 text-spanishGray'>
                  No{' '}
                  <span className='font-semibold text-chineseBlack'>
                    {clinicalTrialInventoryMapper[type]}
                  </span>{' '}
                  here yet! <br /> Click the button to create a{' '}
                  {clinicalTrialInventoryMapper[type]}!
                </span>
              }
            />
            )
          }
        />
      </form>
      {createItem && !isPM && (
        <CreateItemClinicalTrialInventory
          type={type}
          close={() => setCreateItem(false)}
          navigateToItem={(item: ClinicalTrialInventoryBaseItemTypeState) =>
            navigate(`${location.pathname}/:itemId`, { state: item })
          }
        />
      )}
      <Modal
        data-testid={'delete-inventory-item'}
        open={!!currentItemId}
        title='Delete Task'
        onCancel={() => setCurrentItemId(undefined)}
        cancelText={'Cancel'}
        onOk={deleteItem}
        okText={'Delete'}
        width={440}
        centered
        maskClosable={false}
        mask={false}
        okClassName={twMerge('ant-btn-dangerous')}
        titleClassName='text-left'
        classNames={{
          header: 'text-left',
          content: 'text-center !rounded-[2.4rem] !p-[4rem]',
          footer: 'flex justify-center',
        }}
      >
        Are you sure you want to delete <br />
        item?
      </Modal>
    </div>
  );
};
